import { deleteEntity, updateEntities } from '@redux/entities'
import { Schemas } from '@redux/schema'
import { normalize } from 'normalizr'

import api from '@functions/api'

import { CustomFieldSetModel } from '@models/customFieldSet'

import type {
  Action, DeleteParams, EntityOptions,
  Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'

import { AppDispatch } from '@redux/store'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/customFieldSet/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/customFieldSet/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/customFieldSet/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/customFieldSet/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/customFieldSet/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/customFieldSet/CREATE_FAILURE'

const DELETE_REQUEST = 'realbase/customFieldSet/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/customFieldSet/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/customFieldSet/DELETE_FAILURE'

export type CustomFieldSetModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loading: boolean,
}

// Initial State
const initialState: CustomFieldSetModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loading: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadCustomFieldSets(options: EntityOptions) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    // @ts-expect-error TS2461
    const loadedForKeys = [...getState().contracts.loadedForKeys]
    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/custom_field_sets.json', options)
      .then(({ data }: { data: CustomFieldSetModel[] }) => {
        const normalizedJson = normalize(data, Schemas.CUSTOM_FIELD_SET_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createCustomFieldSet(customFieldSet: CustomFieldSetModel, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      custom_field_set: customFieldSet,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/custom_field_sets.json', options, config)
      .then(({ data }: { data: CustomFieldSetModel }) => {
        const normalizedJson = normalize(data, Schemas.CUSTOM_FIELD_SET)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteCustomFieldSet(customFieldSet: DeleteParams<CustomFieldSetModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/custom_field_sets/${customFieldSet.id}.json`, {}, config)
      .then(({ data }: { data: { id: number }}) => {
        const normalizedJson = normalize(data, Schemas.CUSTOM_FIELD_SET)
        // @ts-expect-error TS2345
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })
    return promise
  }
}

// Reducer
export default function reducer(state: CustomFieldSetModuleState = initialState, action: Action = { type: '' }) {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors,
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors,
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors,
      }
    default:
      return state
  }
}
